import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {ROUTE_PATHS} from '@di-shared/routes';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import useAvailableComponentEu from '@di-shared/features/home/eu/useAvailableComponent.eu';
import SafeDrivingSectionEu from '@di-shared/features/home/eu/driving/SafeDrivingSection.eu';
import FuelEfficiencySectionEu from '@di-shared/features/home/eu/driving/FuelEfficiencySection.eu';
import DrivingDistanceSectionEu from '@di-shared/features/home/eu/driving/DrivingDistanceSection.eu';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Autoplay, Pagination} from 'swiper/modules';

interface DrivingContentsEuProps {
  vin: string;
}

function DrivingContentsEu({vin}: DrivingContentsEuProps) {
  const {t} = useInferableTranslation();
  const {checkAvailableComponent} = useAvailableComponentEu({vin});

  return (
    <section className="section">
      {/* 안전운전점수 */}
      {checkAvailableComponent('SAFETY_SCORE') && (
        <ErrorBoundary fallback={<SafeDrivingSectionEu.Error vin={vin} />}>
          <Suspense fallback={<SafeDrivingSectionEu.Loading />}>
            <SafeDrivingSectionEu vin={vin} />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* 서비스 소개 배너 */}
      <div className="section section_trip_history">
        <div className="trip_history_grid">
          <div className="trip_history_grid_item">
            <Swiper
              slidesPerView={'auto'}
              className={'block-banner-swiper'}
              modules={[Pagination, Autoplay]}
              autoplay={{delay: 5000, disableOnInteraction: false}}
              pagination={{el: '.swiper-pagination', type: 'bullets'}}
              spaceBetween={18}
              observer
              observeParents
              loop
            >
              <SwiperSlide>
                <div className="driving_banner type_block">
                  <LinkTo
                    className="driving_banner_box"
                    to={ROUTE_PATHS.ABOUT.getFullPath()}
                  >
                    <span className="driving_banner_txtbox">
                      <strong
                        className="driving_banner_title body_medium_b"
                        style={{whiteSpace: 'pre-wrap'}}
                      >
                        {t('msg_home_introduction_01')}
                      </strong>
                      <p className="driving_banner_desc body_small_r">{t('msg_home_introduction_02')}</p>
                    </span>
                    <span className="driving_banner_imgbox">
                      <ImageSelector
                        className="driving_banner_img"
                        alt="driving_banner_img"
                        kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eu_home_intro_block_light.png')}
                        kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eu_home_intro_block.png')}
                        hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eu_home_intro_block.png')}
                        hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eu_home_intro_block.png')}
                        genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eu_home_intro_block.png')}
                        genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eu_home_intro_block.png')}
                      />
                    </span>
                  </LinkTo>
                </div>
              </SwiperSlide>
              <div className="swiper-pagination" />
            </Swiper>

            {/* 평균 연비 */}
            {checkAvailableComponent('TRIPS') && (
              <ErrorBoundary fallback={<FuelEfficiencySectionEu.Error />}>
                <Suspense fallback={<FuelEfficiencySectionEu.Loading />}>
                  <FuelEfficiencySectionEu vin={vin} />
                </Suspense>
              </ErrorBoundary>
            )}
          </div>
          <div className="trip_history_grid_item">
            {/* 평균 주행 */}
            {checkAvailableComponent('TRIPS') && (
              <ErrorBoundary fallback={<DrivingDistanceSectionEu.Error />}>
                <Suspense fallback={<DrivingDistanceSectionEu.Loading />}>
                  <DrivingDistanceSectionEu vin={vin} />
                </Suspense>
              </ErrorBoundary>
            )}

            {/* 데이터 알아보기 배너 */}
            <div className="driving_banner type_block">
              <LinkTo
                className="driving_banner_box"
                to={ROUTE_PATHS.ABOUT_DATA_EXPLORE.getFullPath()}
              >
                <span className="driving_banner_txtbox type_reverse">
                  <strong
                    className="driving_banner_title body_medium_b"
                    style={{whiteSpace: 'pre-wrap'}}
                  >
                    {t('msg_home_knowingthedata_02')}
                  </strong>
                  <span className="driving_banner_desc body_small_r">{t('msg_home_knowingthedata_01')}</span>
                </span>
                <span className="driving_banner_imgbox">
                  <ImageSelector
                    className="driving_banner_img"
                    alt="driving_banner_img"
                    kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_bar_graph_block_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_bar_graph_block.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_bar_graph_block_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_bar_graph_block.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_bar_graph_block_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_bar_graph_block.png')}
                  />
                </span>
              </LinkTo>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrivingContentsEuLoading() {
  const {t} = useInferableTranslation();
  return (
    <section className="section">
      {/* 안전운전점수 */}
      <SafeDrivingSectionEu.Loading />

      {/* 서비스 소개 배너 */}
      <section className="section section_trip_history">
        <div className="trip_history_grid">
          <div className="trip_history_grid_item">
            <div className="driving_banner type_block">
              <div className="driving_banner_box skeleton">
                <span className="driving_banner_txtbox">
                  <strong
                    className="driving_banner_title body_medium_b"
                    style={{whiteSpace: 'pre-wrap'}}
                  >
                    {t('msg_home_introduction_01')}
                  </strong>
                  <span className="driving_banner_desc body_small_r">{t('msg_home_introduction_02')}</span>
                </span>
                <span className="driving_banner_imgbox">
                  <ImageSelector
                    className="driving_banner_img"
                    alt="driving_banner_img"
                    kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eu_home_intro_block.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eu_home_intro_block.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eu_home_intro_block.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eu_home_intro_block.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eu_home_intro_block.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eu_home_intro_block.png')}
                  />
                </span>
              </div>
            </div>

            {/* 평균 연비 */}
            <FuelEfficiencySectionEu.Loading />
          </div>
          <div className="trip_history_grid_item">
            {/* 평균 주행 */}
            <DrivingDistanceSectionEu.Loading />

            {/* 데이터 알아보기 배너 */}
            <div className="driving_banner type_block skeleton">
              <div className="driving_banner_box">
                <span className="driving_banner_txtbox type_reverse">
                  <strong className="driving_banner_title body_medium_b">{t('msg_home_knowingthedata_01')}</strong>
                  <span className="driving_banner_desc body_small_r">{t('msg_home_knowingthedata_02')}</span>
                </span>
                <span className="driving_banner_imgbox">
                  <ImageSelector
                    className="driving_banner_img"
                    alt="driving_banner_img"
                    kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_bar_graph_block_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_bar_graph_block.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_bar_graph_block_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_bar_graph_block.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_bar_graph_block_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_bar_graph_block.png')}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}

DrivingContentsEu.Loading = () => <DrivingContentsEuLoading />;

export default DrivingContentsEu;
